/**
 * The Chronicle — the things that happen to a settlement between waves.
 *
 * House rules, learned from the relic deck:
 *
 *   1. **Two or three choices, and they must actually differ.** Not three sizes
 *      of the same buff. Each choice on an event should move a *different*
 *      resource: coin against people, morale against permanence, a certain small
 *      loss against an uncertain large one.
 *   2. **There is always a way out that costs nothing but pride.** Every event
 *      carries at least one choice with no `cost`, so a broke settlement can
 *      never be deadlocked in front of a modal it cannot pay for. There is a
 *      test for this.
 *   3. **`rules` is the exact mechanical text.** The player reads it and knows
 *      the number. Flavour is the paragraph above it and is allowed to be
 *      evasive; the rules line is not.
 *   4. **Nothing here calls `Math.random`.** A choice that gambles draws from
 *      `w.rng`, whose cursor is hashed, so a run where the coin came up
 *      differently is a visibly different run.
 *
 * Events fire from the Invasion Director at the end of a Siege — see
 * `maybeDrawChronicle` in `src/sim/systems/director.ts`. While one is pending
 * the world is frozen; `stepWorld` returns early and the only command that gets
 * through is the answer.
 */

import { LogTone } from '../sim/events.ts';
import {
  addResources,
  Team,
  TaxPolicy,
  UF,
  type ResourceBundle,
} from '../sim/types.ts';
import {
  recomputeManaCap,
  recomputePopCap,
  recomputeStorage,
  removeBuilding,
  spawnUnit,
  type World,
} from '../sim/world.ts';
import { createCompany, fillCompany } from '../sim/systems/companies.ts';
import { buildingType } from './buildings.ts';
import { unitType, unitTypeIndex, VILLAGER_TYPE } from './units.ts';
import { chance } from '../core/rng.ts';
import { clamp } from '../core/math.ts';

export interface ChronicleChoice {
  /** Stable across saves — the resolution is recorded as `eventId>choiceId`. */
  id: string;
  /** What the player clicks. Imperative, short. */
  label: string;
  /** Exact mechanics. No vagueness; the tooltip rule applies here too. */
  rules: string;
  /**
   * Charged before `apply` runs, and the choice cannot be taken without it.
   * At least one choice per event must be free — see the deck test.
   */
  cost?: Partial<ResourceBundle>;
  /** Hidden entirely when this returns false. Rare; prefer always-offered. */
  when?: (w: World) => boolean;
  tone?: number;
  /** Mutates the world and returns the line for the chronicle log. */
  apply: (w: World) => string;
}

/**
 * What sort of thing this is. Not a mechanic — a shape, so the deck can be read
 * at a glance and so a run can be checked for having more than one flavour of
 * bad news in it.
 */
export type ChronicleKind = 'settlement' | 'military' | 'faction' | 'lore';

export interface ChronicleEventDef {
  id: string;
  title: string;
  kind: ChronicleKind;
  /** One line for the log, before the modal opens. */
  summary: string;
  /** The paragraph. Grim, dry, concrete. */
  flavour: string;
  /** Relative draw weight among everything currently eligible. */
  weight: number;
  /**
   * How many times this may fire in a single run. One by default.
   *
   * A harvest, a fire and a wind off the Rotmoot ground are things a settlement
   * lives through more than once; a claimant to a throne is not. The counter is
   * read off `w.eventHistory`, so it costs no new state and survives a save for
   * free.
   */
  repeat?: number;
  /**
   * The gate. Reads only state the simulation already keeps — wave, population,
   * treasury, arrears, morale, rubble, standing companies, buildings, relics,
   * the hero, whether an Age is being paid for, and what answered what.
   */
  when: (w: World) => boolean;
  choices: ChronicleChoice[];
}

// ── Small levers, so the deck reads as decisions rather than plumbing ───────

function give(w: World, r: Partial<ResourceBundle>): void {
  addResources(w.res, r);
}

/** Never takes a resource below zero, and reports what it actually got. */
function take(w: World, key: keyof ResourceBundle, amount: number): number {
  const got = Math.min(w.res[key], Math.max(0, amount));
  w.res[key] -= got;
  return got;
}

function mood(w: World, d: number): void {
  w.morale = clamp(w.morale + d, 0, 1.4);
}

/** Scatter new arrivals around the Hearth, deterministically. */
function addVillagers(w: World, n: number): number {
  let made = 0;
  for (let i = 0; i < n; i++) {
    const ang = ((i * 2.399963) % (Math.PI * 2)) - Math.PI;
    const r = 3.4 + (i % 4) * 0.6;
    const idx = spawnUnit(
      w,
      VILLAGER_TYPE,
      Team.Player,
      w.map.hearthX + 0.5 + Math.cos(ang) * r,
      w.map.hearthY + 0.5 + Math.sin(ang) * r,
    );
    if (idx >= 0) made++;
  }
  if (made > 0) w.dirty.jobs = true;
  return made;
}

/** Take people off the roll. Slot order, so it is the same every replay. */
function loseVillagers(w: World, n: number): number {
  const u = w.units;
  let gone = 0;
  for (let i = 0; i < u.slots.high && gone < n; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    u.kill(i);
    gone++;
  }
  if (gone > 0) w.dirty.jobs = true;
  return gone;
}

function villagerCount(w: World): number {
  const u = w.units;
  let n = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0 || u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) !== 0) n++;
  }
  return n;
}

function playerCompanies(w: World) {
  return w.companies.filter((c) => c.team === Team.Player && !c.dead && c.members.length > 0);
}

/**
 * The chain rules.
 *
 * Both read `w.eventHistory`, which is already hashed and already saved, so a
 * consequence that arrives four waves after the decision costs no new state and
 * cannot desync. `timesFired` is what makes a card repeatable; `answered` is
 * what lets a stranger you turned away come back with company.
 */
function timesFired(w: World, eventId: string): number {
  const prefix = `${eventId}>`;
  let n = 0;
  for (const h of w.eventHistory) if (h.startsWith(prefix)) n++;
  return n;
}

function answered(w: World, key: string): boolean {
  return w.eventHistory.includes(key);
}

/** Player buildings that are finished and standing, the Hearth excluded. */
function standingBuildings(w: World): number {
  const b = w.buildings;
  let n = 0;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    if (i === w.hearth || b.build[i] < 1) continue;
    n++;
  }
  return n;
}

/**
 * Take fire to `n` buildings, worst-first by nothing at all — slot order, so a
 * replay burns the same sheds. The Hearth is never on the list: the Hearth going
 * out ends the run, and no event is allowed to end a run without a fight.
 */
function scorch(w: World, n: number, fraction: number): number {
  const b = w.buildings;
  let hit = 0;
  for (let i = 0; i < b.slots.high && hit < n; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    if (i === w.hearth || b.build[i] < 1) continue;
    const want = b.maxHp[i] * fraction;
    if (b.hp[i] <= want) continue;
    b.hp[i] = Math.max(1, want);
    hit++;
  }
  return hit;
}

/** Pull one down. The newest standing thing that is not the Hearth. */
function raze(w: World): string {
  const b = w.buildings;
  let target = -1;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    if (i === w.hearth) continue;
    target = i;
  }
  if (target < 0) return 'nothing anybody would miss';
  const name = buildingType(b.typeId[target]).name;
  removeBuilding(w, target, true);
  recomputeStorage(w);
  recomputePopCap(w);
  recomputeManaCap(w);
  return name;
}

function soldierCount(w: World): number {
  let n = 0;
  for (const c of playerCompanies(w)) n += c.members.length;
  return n;
}

/** Off the roll, largest company first, so it is felt where it can be borne. */
function loseSoldiers(w: World, n: number): number {
  let gone = 0;
  const list = playerCompanies(w).sort((a, b) => b.members.length - a.members.length);
  for (const c of list) {
    for (let k = c.members.length - 1; k >= 0 && gone < n; k--) {
      const i = w.units.resolve(c.members[k]);
      if (i < 0) continue;
      w.units.kill(i);
      gone++;
    }
    if (gone >= n) break;
  }
  return gone;
}

function grantWill(w: World, n: number): void {
  w.will = clamp(w.will + n, 0, w.willMax);
}

/** The largest standing company, or undefined if the wall is held by nobody. */
function biggestCompany(w: World) {
  const list = playerCompanies(w);
  if (list.length === 0) return undefined;
  let best = list[0];
  for (const c of list) if (c.members.length > best.members.length) best = c;
  return best;
}

/**
 * Re-weight the wave the director has already planned.
 *
 * The composition is public on the scouting panel during the whole build phase,
 * so a choice that changes it is a choice the player can see the consequence of
 * before they commit to a defence. That is the fairness rule, kept.
 */
function scaleIncoming(w: World, mul: number): void {
  for (const c of w.incomingComposition) c.count = Math.max(1, Math.round(c.count * mul));
  w.incomingBudget *= mul;
}

/** The heaviest thing this faction can field that stands in a line and holds. */
function lineUnitFor(w: World): number {
  let best = -1;
  let bestThreat = -1;
  for (const id of w.faction.roster) {
    let idx: number;
    try {
      idx = unitTypeIndex(id);
    } catch {
      continue;
    }
    const t = unitType(idx);
    if (t.tier > w.age) continue;
    if ((t.flags & (UF.Ranged | UF.Caster | UF.Siege | UF.Champion | UF.Villager)) !== 0) continue;
    if (t.threat > bestThreat) {
      bestThreat = t.threat;
      best = idx;
    }
  }
  if (best < 0) {
    try {
      best = unitTypeIndex(w.faction.roster[0]);
    } catch {
      best = VILLAGER_TYPE;
    }
  }
  return best;
}

// ── The deck ───────────────────────────────────────────────────────────────

export const CHRONICLE: ChronicleEventDef[] = [
  {
    id: 'gate_refugees',
    kind: 'settlement',
    title: 'They Came Up The Old Road',
    summary: 'There are people at the north stakes.',
    flavour:
      'Forty of them at the stakes with carts and children and nothing else. Their village is a smudge on the horizon that has stopped smoking. They have walked three days and they are asking, politely, in the rain, for the gate.',
    weight: 5,
    when: (w) => w.wave >= 2,
    choices: [
      {
        id: 'open',
        label: 'Open it.',
        rules: '+8 villagers. They arrive with nothing: −140 grain.',
        apply: (w) => {
          const n = addVillagers(w, 8);
          const g = take(w, 'grain', 140);
          mood(w, 0.08);
          return `${n} more on the roll, and ${Math.round(g)} grain gone by nightfall.`;
        },
      },
      {
        id: 'sort',
        label: 'Take the children and anyone with a trade.',
        rules: '+3 villagers. −50 grain. Morale +0.10.',
        apply: (w) => {
          const n = addVillagers(w, 3);
          take(w, 'grain', 50);
          mood(w, 0.1);
          return `${n} taken in. The rest were pointed at the next town, which is also gone.`;
        },
      },
      {
        id: 'bar',
        label: 'Bar it.',
        rules: '+110 grain and +60 timber from what they abandon. Morale −0.22.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { grain: 110, timber: 60 });
          mood(w, -0.22);
          return 'They left the carts. Nobody on the wall watched them go, which took some doing.';
        },
      },
    ],
  },

  {
    id: 'wage_ledger',
    kind: 'military',
    title: 'The Ledger Does Not Balance',
    summary: 'The paymaster wants a word.',
    flavour:
      'He puts the book on the table, open, at the page, and then says nothing at all — which is worse than shouting, and he knows it. The garrison has been paid in promises for eleven days.',
    weight: 4,
    when: (w) => w.wave >= 3 && playerCompanies(w).length > 0 && (w.arrears > 6 || w.res.gold < 70),
    choices: [
      {
        id: 'reliquary',
        label: 'Pay them out of the reliquary.',
        rules: 'Costs 90 Souls. Arrears cleared. Morale +0.08.',
        cost: { souls: 90 },
        apply: (w) => {
          w.arrears = 0;
          mood(w, 0.08);
          return 'The bones bought a fortnight. They will not buy a second one.';
        },
      },
      {
        id: 'tithe',
        label: 'Raise the tithe and pay them in coin.',
        rules: 'Tax set to Hard. Arrears cleared. Morale −0.14.',
        apply: (w) => {
          w.taxPolicy = TaxPolicy.Hard;
          w.arrears = 0;
          mood(w, -0.14);
          return 'The garrison is paid. The people who paid them are outside, counting.';
        },
      },
      {
        id: 'wait',
        label: 'Tell them to wait.',
        rules: 'Arrears doubled. Every company loses a quarter of its veterancy.',
        tone: LogTone.Bad,
        apply: (w) => {
          w.arrears = Math.max(20, w.arrears * 2);
          for (const c of playerCompanies(w)) c.xp *= 0.75;
          return 'They waited. Something went out of them while they did it.';
        },
      },
    ],
  },

  {
    id: 'granary_mould',
    kind: 'settlement',
    title: 'Black In The Grain',
    summary: 'The granary has gone over.',
    flavour:
      'It starts at the bottom of the third bin where nobody looks, and by the time anybody looks it is in the fourth and the fifth as well. The smell is sweet. Sweet is the wrong smell.',
    weight: 4,
    repeat: 2,
    when: (w) => w.wave >= 3 && w.res.grain >= 220,
    choices: [
      {
        id: 'burn',
        label: 'Burn the affected bins.',
        rules: '−40% grain. Nothing else follows.',
        apply: (w) => {
          const lost = take(w, 'grain', w.res.grain * 0.4);
          return `${Math.round(lost)} grain on the fire. It burned wet and took all afternoon.`;
        },
      },
      {
        id: 'bake',
        label: 'Bake it and say nothing.',
        rules: '−25% grain, +half that in bread. Morale −0.25, and all production −10% for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = take(w, 'grain', w.res.grain * 0.25);
          give(w, { bread: lost * 0.5 });
          mood(w, -0.25);
          w.mods.resourceRate *= 0.9;
          return 'Everyone ate. Everyone was ill. Everyone worked out why.';
        },
      },
      {
        id: 'sell',
        label: 'Sell it downriver.',
        rules: '−45% grain, +gold at a third of the honest price. Morale −0.06.',
        apply: (w) => {
          const lost = take(w, 'grain', w.res.grain * 0.45);
          give(w, { gold: lost * 0.32 });
          mood(w, -0.06);
          return `${Math.round(lost)} grain sold to a man who did not ask questions and did not smell it.`;
        },
      },
    ],
  },

  {
    id: 'ash_preacher',
    kind: 'lore',
    title: 'The Man On The Cart',
    summary: 'There is a preacher at the well.',
    flavour:
      'He stands on a cart by the well and explains, at length and with evidence, that the Hearth is a furnace and everyone here is fuel. The trouble is that the crowd is listening. The trouble after that is that he is not entirely wrong.',
    weight: 4,
    when: (w) => w.wave >= 4 && w.morale < 0.62,
    choices: [
      {
        id: 'hang',
        label: 'Hang him at the stakes.',
        rules: 'Morale −0.12. Your companies never rout above 30% strength for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.12);
          w.mods.moraleFloor = Math.max(w.mods.moraleFloor, 0.3);
          return 'A quiet settlement is not the same as a calm one, but it holds a line better.';
        },
      },
      {
        id: 'listen',
        label: 'Let him finish.',
        rules: 'Morale +0.26. All production −8% for the rest of the run.',
        apply: (w) => {
          mood(w, 0.26);
          w.mods.resourceRate *= 0.92;
          return 'He spoke until dark. Very little else was done that day, or the next.';
        },
      },
      {
        id: 'endow',
        label: 'Give him the chapel and a stipend.',
        rules: 'Costs 140 gold. Morale +0.14, +140 Souls, grain upkeep +20%.',
        cost: { gold: 140 },
        apply: (w) => {
          mood(w, 0.14);
          give(w, { souls: 140 });
          w.mods.grainUpkeep *= 1.2;
          return 'He preaches the same sermon indoors now, and eats rather better while doing it.';
        },
      },
    ],
  },

  {
    id: 'bones_under_rubble',
    kind: 'lore',
    title: 'What The Rubble Was Holding Up',
    summary: 'The clearing crews have stopped work.',
    flavour:
      'They stop at midday and send for you. Under the collapsed row is an older floor, and under that, laid in courses like brick, a great many people who were not buried in a hurry.',
    weight: 4,
    when: (w) => w.wave >= 3 && w.rubbleCount > 0,
    choices: [
      {
        id: 'rites',
        label: 'Rebury them, with the rites.',
        rules: 'Costs 60 timber. Morale +0.22 and +30 Will.',
        cost: { timber: 60 },
        apply: (w) => {
          mood(w, 0.22);
          w.will = Math.min(w.willMax, w.will + 30);
          return 'Two days of coffins. The settlement worked through the night to make them.';
        },
      },
      {
        id: 'render',
        label: 'Render them at the furnace.',
        rules: '+240 Souls. Morale −0.20.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { souls: 240 });
          mood(w, -0.2);
          return 'The furnace ran hot for a week. Nobody stood downwind of it.';
        },
      },
      {
        id: 'course',
        label: 'Course them into the wall.',
        rules: 'Walls +18% health for the rest of the run. Morale −0.10.',
        apply: (w) => {
          w.mods.wallHp *= 1.18;
          mood(w, -0.1);
          return 'They were already stacked like masonry. Somebody said so out loud, which did not help.';
        },
      },
    ],
  },

  {
    id: 'free_company',
    kind: 'military',
    title: 'The Free Company At The Ford',
    summary: 'Sellswords are camped a mile out.',
    flavour:
      'Eighty of them, well fed and better armed than anything you own. Their captain carries a letter of marque from a crown that has not existed for six years and is happy to unroll it for you twice.',
    weight: 4,
    when: (w) => w.wave >= 5 && w.res.gold >= 90,
    choices: [
      {
        id: 'hire',
        label: 'Hire them.',
        rules: 'Costs 240 gold. A full company musters at the Hearth, already blooded.',
        cost: { gold: 240 },
        apply: (w) => {
          const idx = lineUnitFor(w);
          const t = unitType(idx);
          const c = createCompany(w, idx, Team.Player, w.map.hearthX + 0.5, w.map.hearthY + 3.5);
          fillCompany(w, c, c.maxSize, w.map.hearthX + 0.5, w.map.hearthY + 3.5);
          c.xp = 420;
          return `${c.name} takes your coin and your colours — ${t.name}, and they know the work.`;
        },
      },
      {
        id: 'divert',
        label: 'Pay them to ride at the horde instead.',
        rules: 'Costs 150 gold. The coming wave loses a quarter of its strength.',
        cost: { gold: 150 },
        apply: (w) => {
          scaleIncoming(w, 0.75);
          return 'They rode east. Something came back along that road later and it was not them.';
        },
      },
      {
        id: 'refuse',
        label: 'Refuse, and watch the road.',
        rules: 'Free. Morale +0.08.',
        apply: (w) => {
          mood(w, 0.08);
          return 'They broke camp at dawn. The settlement noticed you did not sell the harvest to strangers.';
        },
      },
    ],
  },

  {
    id: 'the_well',
    kind: 'lore',
    title: 'Something In The Well',
    summary: 'The water has gone bad.',
    flavour:
      'Two days of foul water, and then a boy goes down on a rope to find out why. He comes back up considerably faster than he went down. Whatever is at the bottom has been at the bottom since before the wall went up.',
    weight: 3,
    when: (w) => w.wave >= 6 && villagerCount(w) >= 14,
    choices: [
      {
        id: 'fill',
        label: 'Fill it in and dig another.',
        rules: 'Costs 130 stone. Nothing further happens.',
        cost: { stone: 130 },
        apply: () => 'Rubble to the brim, a slab on top, and a new shaft forty paces west. Dull. Correct.',
      },
      {
        id: 'fish',
        label: 'Send men down with hooks.',
        rules: 'A gamble, decided now: either +200 Souls and morale +0.12, or four dead and morale −0.28.',
        apply: (w) => {
          // Drawn from the world stream. The cursor is hashed, so a run where
          // this came up the other way is a visibly different run.
          if (chance(w.rng, 0.5)) {
            give(w, { souls: 200 });
            mood(w, 0.12);
            return 'They brought it up in four pieces and burned three of them. The fourth went to the furnace.';
          }
          const lost = loseVillagers(w, 4);
          mood(w, -0.28);
          return `${lost} went down. The rope came back up on its own.`;
        },
      },
      {
        id: 'board',
        label: 'Board it over and tell nobody.',
        rules: 'Free. All production −6% for the rest of the run.',
        apply: (w) => {
          w.mods.resourceRate *= 0.94;
          return 'Planks, nails, and a story about subsidence. The water is carted now, from further out.';
        },
      },
    ],
  },

  {
    id: 'iron_void',
    kind: 'lore',
    title: 'A Seam Where There Was None',
    summary: 'The quarry has broken into something.',
    flavour:
      'The crew breaks into a void that is not a cave. There is iron in it — a great deal of iron, in shapes that pressure does not make — and the crew would like, respectfully, to be reassigned to the farms.',
    weight: 3,
    when: (w) => w.wave >= 4,
    choices: [
      {
        id: 'work',
        label: 'Work it hard.',
        rules: '+380 iron now. All production +8% for the rest of the run. Morale −0.16.',
        apply: (w) => {
          give(w, { iron: 380 });
          w.mods.resourceRate *= 1.08;
          mood(w, -0.16);
          return 'Three shifts, no days. The iron comes out warm, which the smiths have stopped mentioning.';
        },
      },
      {
        id: 'seal',
        label: 'Take what is loose and seal it.',
        rules: '+150 iron. Nothing else.',
        apply: (w) => {
          give(w, { iron: 150 });
          return 'Sealed with a course of stone and a mark on the door that nobody taught anybody to make.';
        },
      },
      {
        id: 'acolytes',
        label: 'Send the acolytes down first.',
        rules: 'Costs 80 Souls. +260 iron, +40 Will, morale +0.10.',
        cost: { souls: 80 },
        apply: (w) => {
          give(w, { iron: 260 });
          w.will = Math.min(w.willMax, w.will + 40);
          mood(w, 0.1);
          return 'They were down there a day and a night and came back with the iron and no explanation.';
        },
      },
    ],
  },

  {
    id: 'deserters',
    kind: 'military',
    title: 'Three Gone Over The Wall',
    summary: 'Three of the garrison are missing.',
    flavour:
      'Missing with their kit, in the small hours, over the eastern stretch where the ditch is shallow. Everyone in the barracks knows which three and which way. Nobody is saying.',
    weight: 3,
    when: (w) => w.wave >= 7 && w.morale < 0.65 && playerCompanies(w).length > 0,
    choices: [
      {
        id: 'hunt',
        label: 'Bring them back.',
        rules: 'Morale −0.12. Companies gain veterancy 40% faster for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.12);
          w.mods.veterancyRate *= 1.4;
          return 'Two came back. The garrison was made to watch, and has been very attentive since.';
        },
      },
      {
        id: 'let_go',
        label: 'Let them go and say nothing.',
        rules: 'Your smallest company loses a third of its models. Morale +0.14.',
        apply: (w) => {
          const list = playerCompanies(w);
          let worst = list[0];
          for (const c of list) if (c.members.length < worst.members.length) worst = c;
          const n = Math.max(1, Math.floor(worst.members.length / 3));
          for (let k = 0; k < n; k++) {
            const h = worst.members[worst.members.length - 1 - k];
            const i = w.units.resolve(h);
            if (i >= 0) w.units.kill(i);
          }
          mood(w, 0.14);
          return `${worst.name} is ${n} lighter by morning. Nobody was surprised and nobody was punished.`;
        },
      },
      {
        id: 'backpay',
        label: 'Pay the back wages first, then ask.',
        rules: 'Costs 180 gold. Arrears cleared, morale +0.18, reinforcements cost 15% less.',
        cost: { gold: 180 },
        apply: (w) => {
          w.arrears = 0;
          mood(w, 0.18);
          w.mods.reinforceCost *= 0.85;
          return 'They came back on their own on the third day, which the sergeants find harder to forgive.';
        },
      },
    ],
  },

  {
    id: 'tribute_rider',
    kind: 'military',
    title: 'A Rider Under A White Rag',
    summary: 'Someone is demanding tribute.',
    flavour:
      'He does not dismount. The list is specific — grain, iron, and forty of your people, some of them by name — and he says his master will collect it either way. He is not lying, and he is not the worst thing on that road.',
    weight: 3,
    when: (w) => w.wave >= 8,
    choices: [
      {
        id: 'pay',
        label: 'Load the carts.',
        rules: 'Costs 220 grain and 130 iron. The coming wave loses a third of its strength. Morale −0.12.',
        cost: { grain: 220, iron: 130 },
        apply: (w) => {
          scaleIncoming(w, 0.66);
          mood(w, -0.12);
          return 'The carts went out at first light and the road stayed quiet for a while.';
        },
      },
      {
        id: 'coin',
        label: 'Pay him in coin instead.',
        rules: 'Costs 320 gold. The coming wave loses a fifth of its strength.',
        cost: { gold: 320 },
        apply: (w) => {
          scaleIncoming(w, 0.8);
          return 'He took the coin and looked at the granary on the way out, which is how these things start.';
        },
      },
      {
        id: 'refuse',
        label: 'Send him back short a hand.',
        rules: 'Free. The coming wave gains half again as many. Morale +0.22.',
        tone: LogTone.Dire,
        apply: (w) => {
          scaleIncoming(w, 1.5);
          mood(w, 0.22);
          return 'The settlement has not been this pleased with you, or this close to finding out why it should not be.';
        },
      },
    ],
  },

  {
    id: 'masons_dispute',
    kind: 'settlement',
    title: 'The Masons Down Tools',
    summary: 'No stone is being laid.',
    flavour:
      'It is about the quarry road, and then about the rates, and then about a man named Coll who has been dead two years and whose widow is standing at the back of the room. By the afternoon nothing is being built at all.',
    weight: 3,
    when: (w) => w.wave >= 5,
    choices: [
      {
        id: 'concede',
        label: 'Give them the road and the rates.',
        rules: 'Costs 100 stone and 100 timber. Buildings finish 20% faster for the rest of the run.',
        cost: { stone: 100, timber: 100 },
        apply: (w) => {
          w.mods.buildSpeed *= 1.2;
          mood(w, 0.1);
          return 'The road is metalled by the end of the week and the guild has stopped writing letters.';
        },
      },
      {
        id: 'settle_coll',
        label: "Pay Coll's widow what he was owed.",
        rules: 'Costs 160 gold. Morale +0.20 and buildings cost 8% less for the rest of the run.',
        cost: { gold: 160 },
        apply: (w) => {
          mood(w, 0.2);
          w.mods.buildCost *= 0.92;
          return 'Two years late, in front of the guild, counted out on the table. Work resumed within the hour.';
        },
      },
      {
        id: 'garrison',
        label: 'Put the garrison in the quarry.',
        rules: 'Free. Buildings cost 15% less for the rest of the run. Morale −0.24.',
        tone: LogTone.Bad,
        apply: (w) => {
          w.mods.buildCost *= 0.85;
          mood(w, -0.24);
          return 'Soldiers cut stone badly and cheaply. The masons watched from the wall and said nothing at all.';
        },
      },
    ],
  },

  {
    id: 'spore_wind',
    kind: 'faction',
    title: 'A Wind Off The Rotmoot Ground',
    summary: 'Something has blown in over the fields.',
    flavour:
      'It comes in low for two nights, and in the morning the wheat is furred and grey and the lambs will not walk on it. It will pass. What it leaves will not.',
    weight: 3,
    repeat: 2,
    when: (w) => w.wave >= 9 && w.res.grain >= 80,
    choices: [
      {
        id: 'burn',
        label: 'Burn the fields back to the ditch.',
        rules: '−60% grain. Nothing follows.',
        apply: (w) => {
          const lost = take(w, 'grain', w.res.grain * 0.6);
          return `${Math.round(lost)} grain burned in the field. The fire took the fence line with it.`;
        },
      },
      {
        id: 'harvest',
        label: 'Harvest through it.',
        rules: 'Free. Keep the grain, lose 6 villagers over the following days. Morale −0.18.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = loseVillagers(w, 6);
          mood(w, -0.18);
          return `The harvest is in. ${lost} of the people who brought it in are not.`;
        },
      },
      {
        id: 'ask',
        label: 'Ask the Hearth.',
        rules: 'Costs 170 Souls. Keep the grain, nobody dies, Will regenerates 12% slower for the rest of the run.',
        cost: { souls: 170 },
        apply: (w) => {
          w.mods.willRegen *= 0.88;
          mood(w, 0.06);
          return 'The grey went off the fields in an afternoon. The Hearth has been slower to answer since.';
        },
      },
    ],
  },

  {
    id: 'old_strongbox',
    kind: 'settlement',
    title: 'A Chest In The Ruin',
    summary: 'The crews have turned up a strongbox.',
    flavour:
      'Under the floor of the old counting house, still locked, still heavy, with the previous administration’s seal on it. The previous administration is a stain on the north wall.',
    weight: 4,
    when: (w) => w.wave >= 3,
    choices: [
      {
        id: 'open',
        label: 'Break the seal.',
        rules: '+280 gold.',
        apply: (w) => {
          give(w, { gold: 280 });
          return 'Coin, mostly, and a bundle of receipts for grain that was never delivered.';
        },
      },
      {
        id: 'chapel',
        label: 'Take it to the chapel unopened.',
        rules: '+130 Souls, +20 maximum Will, morale +0.14.',
        apply: (w) => {
          give(w, { souls: 130 });
          w.willMax += 20;
          w.mods.willMax += 20;
          mood(w, 0.14);
          return 'It went in sealed and it has not come out. The Hearth burns a little higher.';
        },
      },
      {
        id: 'split',
        label: 'Split it among the crew that found it.',
        rules: '+90 gold. Morale +0.26.',
        apply: (w) => {
          give(w, { gold: 90 });
          mood(w, 0.26);
          return 'They drank most of it in a night and have worked like the damned ever since.';
        },
      },
    ],
  },
  // ── Settlement ───────────────────────────────────────────────────────────

  {
    id: 'good_harvest',
    kind: 'settlement',
    title: 'A Year That Did Not Fail',
    summary: 'The carts are in three days early.',
    flavour:
      'Nothing went wrong. The rain came when it was wanted and stopped when it was asked to, the blight stayed south of the ridge, and the carts came in three days ahead of the reckoning. Nobody trusts it. The carts came in anyway.',
    weight: 5,
    repeat: 2,
    when: (w) => w.wave >= 3,
    choices: [
      {
        id: 'bins',
        label: 'Into the bins, all of it.',
        rules: '+320 grain.',
        apply: (w) => {
          give(w, { grain: 320 });
          return 'Counted twice, both times by different men, both times the same. Nobody said so out loud.';
        },
      },
      {
        id: 'feast',
        label: 'Set the tables out in the square.',
        rules: '−120 grain. Morale +0.24 and +30 Will.',
        apply: (w) => {
          take(w, 'grain', 120);
          mood(w, 0.24);
          grantWill(w, 30);
          return 'One night. Everyone. The Hearth burned high enough to read by at the far end of the square.';
        },
      },
      {
        id: 'market',
        label: 'Cart the surplus downriver.',
        rules: '−150 grain, +240 gold. Morale −0.06.',
        apply: (w) => {
          take(w, 'grain', 150);
          give(w, { gold: 240 });
          mood(w, -0.06);
          return 'A good price, honestly got. The bins look emptier than the ledger says they are.';
        },
      },
    ],
  },

  {
    id: 'first_birth',
    kind: 'settlement',
    title: 'Born Inside The Wall',
    summary: 'A child was born in the night.',
    flavour:
      'The first one born inside the wall since the wall went up. The mother is a carter off the west row and will not say who the father is, and nobody with any sense is asking. Half the street came out to look at it, in the rain, at four in the morning.',
    weight: 4,
    when: (w) => w.wave >= 5 && villagerCount(w) >= 16 && w.morale >= 0.5,
    choices: [
      {
        id: 'register',
        label: 'Enter it in the register. First name on the page.',
        rules: 'Morale +0.18 and +35 Will.',
        apply: (w) => {
          mood(w, 0.18);
          grantWill(w, 35);
          return 'The chronicler ruled a fresh page for it, which he has not done for anything else.';
        },
      },
      {
        id: 'holiday',
        label: 'Give the row the day.',
        rules: 'Morale +0.28. All production −4% for the rest of the run.',
        apply: (w) => {
          mood(w, 0.28);
          w.mods.resourceRate *= 0.96;
          return 'One day off in eleven waves. They have taken to expecting the next one.';
        },
      },
      {
        id: 'hearth',
        label: 'Carry it three times round the Hearth.',
        rules: 'Costs 60 Souls. Morale +0.12, +60 Will, Will regenerates 8% faster for the rest of the run.',
        cost: { souls: 60 },
        apply: (w) => {
          mood(w, 0.12);
          grantWill(w, 60);
          w.mods.willRegen *= 1.08;
          return 'Three times, widdershins, the way the acolytes said. It did not cry once, which was remarked on.';
        },
      },
    ],
  },

  {
    id: 'ropewalk_fire',
    kind: 'settlement',
    title: 'Fire In The Ropewalk',
    summary: 'The ropewalk is alight.',
    flavour:
      'It goes up at the tarring end, which is where these things start, and it is in the thatch of the row behind it inside four minutes. There is a wind. There is always a wind on the night it matters.',
    weight: 4,
    repeat: 2,
    when: (w) => w.wave >= 4 + timesFired(w, 'ropewalk_fire') * 5 && standingBuildings(w) >= 6,
    choices: [
      {
        id: 'chain',
        label: 'Bucket chain. Everyone, all night.',
        rules: 'Two buildings drop to a third of their health. Morale +0.10, all production −5% for the rest of the run.',
        apply: (w) => {
          const n = scorch(w, 2, 0.33);
          mood(w, 0.1);
          w.mods.resourceRate *= 0.95;
          return `${n} buildings scorched to the frames. Nobody was on their bench the following day.`;
        },
      },
      {
        id: 'firebreak',
        label: 'Pull down the row in front of it.',
        rules: 'One building is razed to rubble. Nothing else burns. Morale −0.10.',
        tone: LogTone.Bad,
        apply: (w) => {
          const name = raze(w);
          mood(w, -0.1);
          return `${name} came down with hooks and rope while the people inside it watched. The fire stopped at the gap.`;
        },
      },
      {
        id: 'sand',
        label: 'Smother it with the wall sand.',
        rules: 'Costs 130 stone. One building drops to half health and nothing else happens.',
        cost: { stone: 130 },
        apply: (w) => {
          scorch(w, 1, 0.5);
          return 'Every barrow of sand banked against the wall, gone in a night. The wall will want it back.';
        },
      },
    ],
  },

  {
    id: 'grain_dispute',
    kind: 'settlement',
    title: 'Two Households, One Field',
    summary: 'There is a dispute over the west strip.',
    flavour:
      'The Kettles have worked it eleven years. The Brands have a paper. The paper is from an office that burned down along with the town it was in, and the Kettles have eleven years and four sons. Both parties have come to you, which means they have already tried everything else.',
    weight: 4,
    when: (w) => w.wave >= 4 && villagerCount(w) >= 14,
    choices: [
      {
        id: 'paper',
        label: 'Uphold the paper.',
        rules: '+90 grain. Morale −0.14.',
        apply: (w) => {
          give(w, { grain: 90 });
          mood(w, -0.14);
          return 'The law was followed. Four sons stood at the back of the hall and followed it too.';
        },
      },
      {
        id: 'split',
        label: 'Split the strip down the middle.',
        rules: '−50 grain re-ditching it. Morale −0.04, and neither household will speak of it again.',
        apply: (w) => {
          take(w, 'grain', 50);
          mood(w, -0.04);
          return 'A ditch down the middle of a good field, and two households equally certain they were robbed.';
        },
      },
      {
        id: 'stakes',
        label: 'Let them settle it at the stakes.',
        rules: 'A gamble, decided now: either morale +0.20, or −4 villagers and morale −0.22.',
        tone: LogTone.Bad,
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            mood(w, 0.2);
            return 'Two rounds, no blades, and a handshake nobody expected. The settlement talked of nothing else.';
          }
          const lost = loseVillagers(w, 4);
          mood(w, -0.22);
          return `${lost} of them are in the ground and the field is still a field. It will be worked by neither.`;
        },
      },
    ],
  },

  {
    id: 'early_cold',
    kind: 'settlement',
    title: 'The Cold Came Early',
    summary: 'There is frost on the inside of the shutters.',
    flavour:
      'Six weeks early, and hard. The turnips are in the ground and they are staying there. The old people have stopped complaining about it, which is the part the quartermaster does not like.',
    weight: 4,
    when: (w) => w.wave >= 6 && w.res.grain < 280,
    choices: [
      {
        id: 'rations',
        label: 'Half rations until it breaks.',
        rules: 'Grain upkeep −25% for the rest of the run. Morale −0.20.',
        apply: (w) => {
          w.mods.grainUpkeep *= 0.75;
          mood(w, -0.2);
          return 'The measure was cut in half and the bowl was not. People noticed which one had changed.';
        },
      },
      {
        id: 'furniture',
        label: 'Burn whatever is not holding a roof up.',
        rules: '−220 timber. Morale +0.14.',
        apply: (w) => {
          take(w, 'timber', 220);
          mood(w, 0.14);
          return 'Benches, looms, two doors and a wagon bed. Warm rooms, and nowhere to sit down in them.';
        },
      },
      {
        id: 'seed',
        label: 'Open the seed corn.',
        rules: '+260 grain now. All production −10% for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { grain: 260 });
          w.mods.resourceRate *= 0.9;
          return 'Everyone ate. The farmers stood at the granary door and watched the sacks go past them.';
        },
      },
    ],
  },

  {
    id: 'stranger_gate',
    kind: 'settlement',
    title: 'A Man Alone On The Road',
    summary: 'There is one man at the south stakes.',
    flavour:
      'No cart, no bundle, no dog. He has walked in from somewhere people do not walk in from, and he is not thin enough for it. He asks for a night by the fire and says he can pay, and does not say with what.',
    weight: 5,
    when: (w) => w.wave >= 3,
    choices: [
      {
        id: 'take',
        label: 'Let him in.',
        rules: 'Morale +0.06. He stays, and does the work nobody asks him to do.',
        apply: (w) => {
          mood(w, 0.06);
          return 'A blanket by the north wall and a place at the second table. He has not said his name.';
        },
      },
      {
        id: 'question',
        label: 'Question him at the guardhouse.',
        rules: 'The coming wave loses a tenth of its strength. Morale −0.08.',
        apply: (w) => {
          scaleIncoming(w, 0.9);
          mood(w, -0.08);
          return 'He knew the ground east of the ford better than your scouts do. He would not say how.';
        },
      },
      {
        id: 'away',
        label: 'Send him on.',
        rules: 'Free. Nothing happens tonight.',
        apply: () =>
          'He went south at dusk without arguing, which the man on the gate has since found hard to stop thinking about.',
      },
    ],
  },

  {
    id: 'stranger_repaid',
    kind: 'settlement',
    title: 'He Paid',
    summary: 'The man from the road wants to show you something.',
    flavour:
      'Three weeks splitting kindling and saying nothing, and this morning he asks for a lamp and a spade and takes four of the watch out past the second ditch to a place he says he remembers. He is right about it. That is worse than if he had been lying.',
    weight: 5,
    when: (w) => answered(w, 'stranger_gate>take') && w.wave >= 7,
    choices: [
      {
        id: 'dig',
        label: 'Dig where he says.',
        rules: '+300 iron and +160 stone.',
        apply: (w) => {
          give(w, { iron: 300, stone: 160 });
          return 'Two days of digging in the wet. He stood at the lip the whole time and did not come down.';
        },
      },
      {
        id: 'acolytes',
        label: 'Have him show the acolytes instead.',
        rules: 'Costs 100 Souls. +220 Souls and Will regenerates 12% faster for the rest of the run.',
        cost: { souls: 100 },
        apply: (w) => {
          give(w, { souls: 220 });
          w.mods.willRegen *= 1.12;
          return 'They were out there a night. He came back with them and has been sleeping in the chapel since.';
        },
      },
      {
        id: 'fill',
        label: 'Fill it in and put him out.',
        rules: 'Free. Morale −0.12 and +45 Will.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.12);
          grantWill(w, 45);
          return 'Backfilled by dark, and he was outside the stakes by the same hour he first stood at them.';
        },
      },
    ],
  },

  {
    id: 'stranger_returns',
    kind: 'settlement',
    title: 'He Came Back With Company',
    summary: 'The man you turned away is on the road again.',
    flavour:
      'Further out this time, and not alone, and not walking. Whatever kept him warm those three weeks has an interest in this settlement now, and a fair idea of the ground, and a grudge the size of one cold night that has been very carefully kept.',
    weight: 6,
    when: (w) => answered(w, 'stranger_gate>away') && w.wave >= 8,
    choices: [
      {
        id: 'buy',
        label: 'Buy him off.',
        rules: 'Costs 260 gold. Nothing follows.',
        cost: { gold: 260 },
        apply: () =>
          'He took it at the ford without dismounting and did not count it, which cost more than the coin did.',
      },
      {
        id: 'hunt',
        label: 'Send the watch out after him.',
        rules: 'A gamble: either the coming wave is a fifth weaker, or you lose four soldiers and it is a fifth stronger.',
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            scaleIncoming(w, 0.8);
            return 'They found the camp at first light and there was nobody left in it worth bringing back.';
          }
          const lost = loseSoldiers(w, 4);
          scaleIncoming(w, 1.2);
          return `${lost} went out. The ones who came back had walked in a circle for six hours and knew it.`;
        },
      },
      {
        id: 'stand',
        label: 'Stand to, and let him come.',
        rules: 'Free. The coming wave is half again as strong. Morale +0.10.',
        tone: LogTone.Dire,
        apply: (w) => {
          scaleIncoming(w, 1.5);
          mood(w, 0.1);
          return 'The gate stayed shut a second time, on purpose, and the settlement was told why.';
        },
      },
    ],
  },

  {
    id: 'coughing_row',
    kind: 'settlement',
    title: 'The Coughing Row',
    summary: 'Something is going through the east houses.',
    flavour:
      'It starts in the east row and it is in the west row inside four days, which is faster than people walk. It takes the strong ones first. That is the detail nobody wants explained to them.',
    weight: 4,
    when: (w) => w.wave >= 8 && villagerCount(w) >= 18,
    choices: [
      {
        id: 'quarantine',
        label: 'Nail the row shut.',
        rules: '−5 villagers. Morale −0.18. It stops there.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = loseVillagers(w, 5);
          mood(w, -0.18);
          return `${lost} behind the boards, and the boards stayed up until the coughing did not.`;
        },
      },
      {
        id: 'physicians',
        label: 'Send for whoever calls themselves a physician.',
        rules: 'Costs 220 gold. −2 villagers. Morale −0.04.',
        cost: { gold: 220 },
        apply: (w) => {
          const lost = loseVillagers(w, 2);
          mood(w, -0.04);
          return `Two carried out. He charged by the visit and made a great many of them. ${lost === 2 ? 'It could have been worse.' : 'It was worse elsewhere.'}`;
        },
      },
      {
        id: 'work',
        label: 'Work through it.',
        rules: 'Free. −10 villagers over the following week. All production +6% for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = loseVillagers(w, 10);
          w.mods.resourceRate *= 1.06;
          mood(w, -0.1);
          return `${lost} names off the roll, and not one bench stood empty a whole day. It was noticed.`;
        },
      },
    ],
  },

  {
    id: 'counting_house_crowd',
    kind: 'settlement',
    title: 'A Crowd At The Counting House',
    summary: 'There are four hundred people in the square.',
    flavour:
      'Not a riot. Nobody is throwing anything and nobody is shouting, which the sergeant of the watch says is the part that worries him. They have stood in the square since the third bell looking at one door, and they have not gone home for their dinner.',
    weight: 6,
    when: (w) => w.wave >= 4 && w.taxPolicy >= TaxPolicy.Hard && w.morale < 0.52,
    choices: [
      {
        id: 'lower',
        label: 'Drop the tithe to Fair, from the step.',
        rules: 'Tax set to Fair. Morale +0.22.',
        apply: (w) => {
          w.taxPolicy = TaxPolicy.Fair;
          mood(w, 0.22);
          return 'Said out loud, in front of all of them, which means it cannot quietly be undone.';
        },
      },
      {
        id: 'farmer',
        label: 'Hand them the tax farmer.',
        rules: '−220 gold that is never accounted for. Morale +0.30.',
        tone: LogTone.Bad,
        apply: (w) => {
          take(w, 'gold', 220);
          mood(w, 0.3);
          return 'The square emptied inside the hour. The books for the last two quarters went with him.';
        },
      },
      {
        id: 'clear',
        label: 'Put the watch across the square.',
        rules: 'Free. −3 villagers. Morale −0.26, and the tithe holds.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = loseVillagers(w, 3);
          mood(w, -0.26);
          return `Cleared by dark. ${lost} did not walk away from it, and the tithe is collected on time now.`;
        },
      },
    ],
  },

  // ── Military ─────────────────────────────────────────────────────────────

  {
    id: 'captured_scout',
    kind: 'military',
    title: 'They Took One Alive',
    summary: 'The night watch has a prisoner.',
    flavour:
      'It was under the fascines by the east ditch counting the stakes, and it has been counting them two nights, and it has a knotted cord to count them with. It is not frightened of you. It is frightened of going back.',
    weight: 5,
    when: (w) => w.wave >= 5 && playerCompanies(w).length > 0,
    choices: [
      {
        id: 'break',
        label: 'Break it, and take the plan.',
        rules: 'The coming wave loses a third of its strength. Morale −0.14.',
        tone: LogTone.Bad,
        apply: (w) => {
          scaleIncoming(w, 0.66);
          mood(w, -0.14);
          return 'It talked at the second hour and kept talking after there was nothing left to ask.';
        },
      },
      {
        id: 'hang',
        label: 'Hang it on the stakes where they can see.',
        rules: 'Morale +0.18. The coming wave gains a tenth of its strength.',
        apply: (w) => {
          mood(w, 0.18);
          scaleIncoming(w, 1.1);
          return 'It hung on the east stakes for three days. Something out there counted it, too.';
        },
      },
      {
        id: 'trade',
        label: 'Trade it back for your own missing.',
        rules: 'Costs 140 gold. +4 villagers. Morale +0.14.',
        cost: { gold: 140 },
        apply: (w) => {
          const n = addVillagers(w, 4);
          mood(w, 0.14);
          return `${n} came back over the ford, and three of them will not say a word about where from.`;
        },
      },
    ],
  },

  {
    id: 'duel_at_stakes',
    kind: 'military',
    title: 'A Standing Challenge At The Stakes',
    summary: 'Something out there wants a single fight.',
    flavour:
      'It comes up to the second ditch every evening at the same hour, plants a pole, hangs a helmet on it and waits until the light goes. The helmet is one of yours. It has been polished.',
    weight: 4,
    when: (w) => w.wave >= 9 && soldierCount(w) >= 6,
    choices: [
      {
        id: 'hero',
        label: 'Answer it yourself.',
        rules: 'A gamble: either morale +0.30, +260 Souls and the coming wave loses a fifth — or morale −0.18 and it gains a tenth.',
        when: (w) => w.hero.classId !== '',
        apply: (w) => {
          if (chance(w.rng, 0.55)) {
            mood(w, 0.3);
            give(w, { souls: 260 });
            scaleIncoming(w, 0.8);
            return 'Four passes and a short one. The pole is still there. The helmet came back inside the wall.';
          }
          mood(w, -0.18);
          scaleIncoming(w, 1.1);
          return 'It was carried back through the gate on a hurdle with the whole wall watching. The pole is still there.';
        },
      },
      {
        id: 'champion',
        label: 'Send the best man in the garrison.',
        rules: 'A gamble: either morale +0.22 and companies gain veterancy 25% faster, or −3 soldiers and morale −0.16.',
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            mood(w, 0.22);
            w.mods.veterancyRate *= 1.25;
            return 'He walked back on his own legs. Every sergeant on the wall has been quoting him since.';
          }
          const lost = loseSoldiers(w, 3);
          mood(w, -0.16);
          return `He did not walk back, and neither did the ${Math.max(1, lost - 1)} who went out to fetch him.`;
        },
      },
      {
        id: 'ignore',
        label: 'Leave the helmet where it is.',
        rules: 'Free. Morale −0.12. The coming wave loses a tenth of its strength — it waited two evenings longer than it meant to.',
        apply: (w) => {
          mood(w, -0.12);
          scaleIncoming(w, 0.9);
          return 'Six evenings. On the seventh it took the pole with it, which nobody on the wall found reassuring.';
        },
      },
    ],
  },

  {
    id: 'sellsword_debt',
    kind: 'military',
    title: 'The Company Wants Its Second Purse',
    summary: 'The sellswords are asking for the rest.',
    flavour:
      'The captain is very polite about it. He produces the letter of marque again and reads out the clause on arrears in a voice that carries as far as the barracks, and eighty men inside your wall stand still and listen to him read it.',
    weight: 6,
    when: (w) => answered(w, 'free_company>hire') && w.wave >= 9,
    choices: [
      {
        id: 'pay',
        label: 'Count it out on the table.',
        rules: 'Costs 300 gold. Morale +0.10, and your largest company gains a great deal of veterancy.',
        cost: { gold: 300 },
        apply: (w) => {
          mood(w, 0.1);
          const c = biggestCompany(w);
          if (c) c.xp += 600;
          return 'Counted out in front of the barracks door, which is where he wanted it counted.';
        },
      },
      {
        id: 'land',
        label: 'Pay them in land instead.',
        rules: 'Free. +6 villagers, your largest company loses a third of its models, morale +0.06.',
        apply: (w) => {
          const c = biggestCompany(w);
          const n = c ? Math.max(1, Math.floor(c.members.length / 3)) : 0;
          if (c) {
            for (let k = 0; k < n; k++) {
              const i = w.units.resolve(c.members[c.members.length - 1 - k]);
              if (i >= 0) w.units.kill(i);
            }
          }
          const made = addVillagers(w, 6);
          mood(w, 0.06);
          return `${n} of them hung the mail on a nail and took a strip apiece. ${made} new names on the roll, none of them soldiers now.`;
        },
      },
      {
        id: 'refuse',
        label: 'Tell him to read the clause again.',
        rules: 'Free. Half your largest company walks out. Morale −0.18, and the coming wave gains a tenth of its strength.',
        tone: LogTone.Dire,
        apply: (w) => {
          const c = biggestCompany(w);
          const n = c ? Math.max(1, Math.floor(c.members.length / 2)) : 0;
          if (c) {
            for (let k = 0; k < n; k++) {
              const i = w.units.resolve(c.members[c.members.length - 1 - k]);
              if (i >= 0) w.units.kill(i);
            }
          }
          mood(w, -0.18);
          scaleIncoming(w, 1.1);
          return `${n} went out the east gate in good order with everything they came with, and a fair idea of the ground.`;
        },
      },
    ],
  },

  {
    id: 'veterans_petition',
    kind: 'military',
    title: 'The Old Sweats Want Land',
    summary: 'A petition out of the barracks.',
    flavour:
      'Eleven names on it, every one of them a man who was on the wall at the first wave and is still on it. None of them can write, so somebody wrote it for them and they made their marks underneath. They want the strip past the second ditch and they want it in writing.',
    weight: 4,
    when: (w) => w.wave >= 11 && soldierCount(w) >= 10,
    choices: [
      {
        id: 'grant',
        label: 'Grant it, sealed.',
        rules: 'Free. −5 soldiers, +7 villagers, morale +0.24, all production +5% for the rest of the run.',
        apply: (w) => {
          const gone = loseSoldiers(w, 5);
          const made = addVillagers(w, 7);
          mood(w, 0.24);
          w.mods.resourceRate *= 1.05;
          return `${gone} off the wall and ${made} onto the strip, and the ditch past it has never been dug so well.`;
        },
      },
      {
        id: 'refuse',
        label: 'Refuse, and post the list on the barracks door.',
        rules: 'Free. Morale −0.16. Companies gain veterancy 30% faster for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.16);
          w.mods.veterancyRate *= 1.3;
          return 'Eleven names where everyone can read them. They have been very hard to fault since, and very quiet.';
        },
      },
      {
        id: 'promise',
        label: 'Buy the strip now and promise it after.',
        rules: 'Costs 200 gold. Morale +0.12, reinforcements cost 12% less for the rest of the run.',
        cost: { gold: 200 },
        apply: (w) => {
          mood(w, 0.12);
          w.mods.reinforceCost *= 0.88;
          return 'Bought, fenced, and not signed over. They walk out to look at it on their off days.';
        },
      },
    ],
  },

  {
    id: 'night_sortie',
    kind: 'military',
    title: 'The Watch Wants To Go Out',
    summary: 'A sortie is being proposed, loudly.',
    flavour:
      'Four score men out at the third bell, burn what is stacked at the ford, back before light. The proposal has been made three times now and each time it gets a little better and a little more popular, which is not the same as getting more likely to work.',
    weight: 5,
    when: (w) => w.wave >= 7 && soldierCount(w) >= 8 && w.morale > 0.5,
    choices: [
      {
        id: 'go',
        label: 'Let them go.',
        rules: 'A gamble: either the coming wave loses a third of its strength and morale +0.20, or −6 soldiers and morale −0.22.',
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            scaleIncoming(w, 0.66);
            mood(w, 0.2);
            return 'Back by the fourth bell with the ford burning behind them and nothing left at the gate to say.';
          }
          const lost = loseSoldiers(w, 6);
          mood(w, -0.22);
          return `Something was waiting at the ford and had been for two nights. ${lost} did not come back over it.`;
        },
      },
      {
        id: 'scouts',
        label: 'Send four men to look, and no further.',
        rules: 'Costs 80 gold in silence money. The coming wave loses a tenth of its strength.',
        cost: { gold: 80 },
        apply: (w) => {
          scaleIncoming(w, 0.9);
          return 'Four went, four came back, and the map of the ford is eleven days newer than it was.';
        },
      },
      {
        id: 'forbid',
        label: 'Forbid it. Post the order.',
        rules: 'Free. Morale −0.14. Walls +12% health for the rest of the run.',
        apply: (w) => {
          mood(w, -0.14);
          w.mods.wallHp *= 1.12;
          return 'They spent the night on the ditch instead, in silence, and the ditch has never been better.';
        },
      },
    ],
  },

  {
    id: 'engineer_offer',
    kind: 'military',
    title: 'A Man Who Says He Builds Engines',
    summary: 'There is an engineer at the gate.',
    flavour:
      'A case of drawings, a burnt hand, and a letter of introduction from a town that paid him and lost its wall anyway. He points out three things wrong with your towers before anybody offers him a chair, and he is right about two of them.',
    weight: 4,
    when: (w) => w.wave >= 8,
    choices: [
      {
        id: 'hire',
        label: 'Hire him and give him the towers.',
        rules: 'Costs 200 gold and 120 iron. Towers deal 25% more damage and reach 1.5 tiles further for the rest of the run.',
        cost: { gold: 200, iron: 120 },
        apply: (w) => {
          w.mods.towerDamage *= 1.25;
          w.mods.towerRange *= 1.12;
          w.mods.archerRange += 1.5;
          return 'He has not slept in a bed since. The east tower now throws further than anyone will admit is sensible.';
        },
      },
      {
        id: 'prove',
        label: 'Let him prove it on one tower.',
        rules: 'A gamble: either towers deal 15% more damage, or −140 timber and morale −0.10.',
        apply: (w) => {
          if (chance(w.rng, 0.55)) {
            w.mods.towerDamage *= 1.15;
            return 'It worked on the third attempt and has worked every time since, which he finds unremarkable.';
          }
          take(w, 'timber', 140);
          mood(w, -0.1);
          return 'The frame went through the tower floor and took the floor below it. He has gone south.';
        },
      },
      {
        id: 'refuse',
        label: 'Send him on south.',
        rules: 'Free. Buildings cost 5% less for the rest of the run — he was right about two of them.',
        apply: (w) => {
          w.mods.buildCost *= 0.95;
          return 'He left the case as security against a bed and a meal, and has not come back for it.';
        },
      },
    ],
  },

  {
    id: 'sergeants_group',
    kind: 'military',
    title: 'The Sergeants Come In A Group',
    summary: 'Five sergeants at the door, together.',
    flavour:
      'Together is the whole message. Separately they are five men with a complaint; together they are something with a name. They stand, they do not sit, and the senior one does the talking and does it badly on purpose, so that none of it can be written down.',
    weight: 5,
    when: (w) => w.wave >= 6 && w.arrears > 20 && playerCompanies(w).length > 0,
    choices: [
      {
        id: 'promote',
        label: 'Promote the quietest one over the rest.',
        rules: 'Free. Arrears halved. Morale −0.08, companies gain veterancy 20% faster for the rest of the run.',
        apply: (w) => {
          w.arrears = Math.floor(w.arrears / 2);
          mood(w, -0.08);
          w.mods.veterancyRate *= 1.2;
          return 'Four of them found out at the same time as the barracks did. It has not come to blows yet.';
        },
      },
      {
        id: 'pay',
        label: 'Pay them, in front of each other.',
        rules: 'Costs 260 gold. Arrears cleared. Morale +0.16.',
        cost: { gold: 260 },
        apply: (w) => {
          w.arrears = 0;
          mood(w, 0.16);
          return 'On the table, by name, in order of service. The senior one counted his twice and apologised for it.';
        },
      },
      {
        id: 'quarter',
        label: 'Quarter them on the town instead.',
        rules: 'Free. Arrears cleared. Morale −0.24 and grain upkeep +25% for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          w.arrears = 0;
          mood(w, -0.24);
          w.mods.grainUpkeep *= 1.25;
          return 'Two to a house down the whole east row. The wage bill is settled and the households are not.';
        },
      },
    ],
  },

  // ── Faction-flavoured ────────────────────────────────────────────────────
  //
  // Two gates are in play here and they are not the same one. `incomingFaction`
  // is who is coming *next* — the director has already planned the wave by the
  // time the Chronicle draws, so it is known and it is on the scouting panel.
  // `w.faction.id` is who the player is, and those cards are about the price of
  // being that.

  {
    id: 'gutter_digging',
    kind: 'faction',
    title: 'Something Is Digging',
    summary: 'The ground under the east row is hollow.',
    flavour:
      'You hear it best with an ear to a spade blade stuck upright in the ground, which is a trick the miners know and wish they did not. It is not one thing digging. It is a great many things digging in a line, and the line points at the granary.',
    weight: 8,
    when: (w) => w.incomingFaction === 'gutter' && w.wave >= 5,
    choices: [
      {
        id: 'flood',
        label: 'Break the cistern into the workings.',
        rules: 'Costs 140 stone. The coming wave loses a third of its strength. Morale −0.06.',
        cost: { stone: 140 },
        apply: (w) => {
          scaleIncoming(w, 0.66);
          mood(w, -0.06);
          return 'It took the cistern wall and a day and a half. The digging stopped, and then started further out.';
        },
      },
      {
        id: 'collapse',
        label: 'Collapse it with whatever is in it.',
        rules: 'The coming wave loses a fifth of its strength. +180 Souls. Morale −0.14.',
        tone: LogTone.Bad,
        apply: (w) => {
          scaleIncoming(w, 0.8);
          give(w, { souls: 180 });
          mood(w, -0.14);
          return 'The east row settled four inches overnight and has not settled further. The sermon went on for two days under it.';
        },
      },
      {
        id: 'watch',
        label: 'Set a watch on the floor and let them come up.',
        rules: 'Free. Morale +0.10. Companies gain veterancy 25% faster for the rest of the run.',
        apply: (w) => {
          mood(w, 0.1);
          w.mods.veterancyRate *= 1.25;
          return 'Spears round the hole, in the dark, for eleven nights. Everyone who stood there is a different soldier now.';
        },
      },
    ],
  },

  {
    id: 'rot_delegation',
    kind: 'faction',
    title: 'A Delegation Of Sores',
    summary: 'The Rotmoot has sent envoys.',
    flavour:
      'Three of them at the second ditch, holding a branch they appear to believe is the correct object for the occasion. One has been elected to speak. The other two are present, as far as anybody can tell, to be quorate. They wish to table a motion regarding your fields.',
    weight: 8,
    when: (w) => w.incomingFaction === 'rotmoot' && w.wave >= 6,
    choices: [
      {
        id: 'hear',
        label: 'Hear the motion out.',
        rules: 'The coming wave loses a quarter of its strength. Morale −0.16, all production −6% for the rest of the run.',
        apply: (w) => {
          scaleIncoming(w, 0.75);
          mood(w, -0.16);
          w.mods.resourceRate *= 0.94;
          return 'It took four hours and was carried by acclamation. Nothing in the west fields has looked right since.';
        },
      },
      {
        id: 'burn',
        label: 'Burn them where they stand.',
        rules: 'Morale +0.20. The coming wave gains a fifth of its strength.',
        apply: (w) => {
          mood(w, 0.2);
          scaleIncoming(w, 1.2);
          return 'They burned wet and slowly and did not appear to mind. The branch is still out there.';
        },
      },
      {
        id: 'amend',
        label: 'Table an amendment, and pay for it.',
        rules: 'Costs 180 grain. The coming wave loses a third of its strength.',
        cost: { grain: 180 },
        apply: (w) => {
          scaleIncoming(w, 0.66);
          return 'Eighteen carts to the tree line. The amendment was accepted, minuted, and read back to you twice.';
        },
      },
    ],
  },

  {
    id: 'drowned_fog',
    kind: 'faction',
    title: 'The Fog Came Up The Ditch',
    summary: 'There is fog in the streets, and it came from the east.',
    flavour:
      'It comes up the ditch against the wind, which is the first wrong thing, and it does not lift at noon, which is the second. Two of the watch walked into it and came out on the same side hours later, insisting they had not been gone.',
    weight: 8,
    when: (w) => w.incomingFaction === 'drowned' && w.wave >= 6,
    choices: [
      {
        id: 'bells',
        label: 'Ring the bells all night, in shifts.',
        rules: 'Morale +0.14, −40 Will, and Will regenerates 10% slower for the rest of the run.',
        apply: (w) => {
          mood(w, 0.14);
          grantWill(w, -40);
          w.mods.willRegen *= 0.9;
          return 'Nobody slept and everybody knew which way the square was, which turned out to be the point.';
        },
      },
      {
        id: 'wax',
        label: 'Stop every ear in the settlement with wax.',
        rules: 'The coming wave loses a fifth of its strength. All production −8% for the rest of the run.',
        apply: (w) => {
          scaleIncoming(w, 0.8);
          w.mods.resourceRate *= 0.92;
          return 'A settlement that cannot hear cannot be called. It also cannot be told the granary is on fire.';
        },
      },
      {
        id: 'listen',
        label: 'Send the acolytes into it to listen.',
        rules: 'Costs 130 Souls. A gamble: either +50 Will, morale +0.16 and the coming wave loses a third — or −6 villagers and morale −0.24.',
        cost: { souls: 130 },
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            grantWill(w, 50);
            mood(w, 0.16);
            scaleIncoming(w, 0.66);
            return 'They came back at dawn with the route it takes inland, written down, in a hand none of them owns.';
          }
          const lost = loseVillagers(w, 6);
          mood(w, -0.24);
          return `They came back. ${lost} people who were not acolytes walked out into it that night and did not.`;
        },
      },
    ],
  },

  {
    id: 'ironfang_name',
    kind: 'faction',
    title: 'A Head On A Pole, And A Name',
    summary: 'The Ironfang have left something at the ford.',
    flavour:
      'A pole, a head, and a name cut into the shaft in letters a foot high. The head is a carter of yours who went out for withies eight days ago. The name is not his. The interpreter says it is what they have decided to call this settlement, and that it means, roughly, the place we are coming back to.',
    weight: 8,
    when: (w) => w.incomingFaction === 'ironfang' && w.wave >= 5,
    choices: [
      {
        id: 'bury',
        label: 'Take him down and bury him properly.',
        rules: '−50 timber. Morale +0.16.',
        apply: (w) => {
          take(w, 'timber', 50);
          mood(w, 0.16);
          return 'A box, a hole, and his name read out. The pole was left standing, which was argued about.';
        },
      },
      {
        id: 'answer',
        label: 'Put a bigger pole beside it.',
        rules: 'Morale +0.24. The coming wave gains a fifth of its strength.',
        tone: LogTone.Dire,
        apply: (w) => {
          mood(w, 0.24);
          scaleIncoming(w, 1.2);
          return 'Twelve feet, and four of theirs on it. They understood it. That was always going to be the risk.';
        },
      },
      {
        id: 'ransom',
        label: 'Send iron to the ford and buy the road.',
        rules: 'Costs 150 iron. The coming wave loses a quarter of its strength. Morale −0.12.',
        cost: { iron: 150 },
        apply: (w) => {
          scaleIncoming(w, 0.75);
          mood(w, -0.12);
          return 'They took the iron and left the pole, and the settlement watched the carts go out past the head.';
        },
      },
    ],
  },

  {
    id: 'litany_letter',
    kind: 'faction',
    title: 'The Court Sends Its Regrets',
    summary: 'A letter, nailed to the gate, in a very good hand.',
    flavour:
      'Put up some time between the second and third bell, on paper that has not been made anywhere for sixty years, in what the clerk says is a court hand and a good one. It regrets that attendance will be required. It gives a date. The date is correct.',
    weight: 8,
    when: (w) => w.incomingFaction === 'litany' && w.wave >= 10,
    choices: [
      {
        id: 'reply',
        label: 'Reply in kind, and seal it.',
        rules: 'Costs 160 Souls. The coming wave loses a quarter of its strength. Morale +0.10.',
        cost: { souls: 160 },
        apply: (w) => {
          scaleIncoming(w, 0.75);
          mood(w, 0.1);
          return 'Sent out with a rider who was told not to wait for an answer, and who waited anyway, and got one.';
        },
      },
      {
        id: 'burn',
        label: 'Burn it in the Hearth.',
        rules: '+60 Will and morale +0.12. The coming wave gains a tenth of its strength.',
        apply: (w) => {
          grantWill(w, 60);
          mood(w, 0.12);
          return 'It went up green and the Hearth stood higher for a week. The date has not moved.';
        },
      },
      {
        id: 'file',
        label: 'File it in the register and say nothing.',
        rules: 'Free. Morale −0.10. The coming wave loses a tenth of its strength.',
        apply: (w) => {
          mood(w, -0.1);
          scaleIncoming(w, 0.9);
          return 'Filed, dated, and shelved. Whatever wrote it appears to consider the matter partly settled.';
        },
      },
    ],
  },

  {
    id: 'karrun_rent',
    kind: 'faction',
    title: 'The Deep Wants Its Rent',
    summary: 'The lower workings have stopped answering.',
    flavour:
      'Karrun-Dûr did not flee the dark; it went down and came to terms. The terms were never written and the deepwrights will not discuss them, but every so often the lower workings go quiet for a day, and afterwards somebody is not at their bench, and the terms are held to have been met.',
    weight: 6,
    when: (w) => w.faction.id === 'karrun' && w.wave >= 6,
    choices: [
      {
        id: 'pay',
        label: 'Let the terms be met.',
        rules: 'Free. −3 villagers. Walls +20% health for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          const lost = loseVillagers(w, 3);
          w.mods.wallHp *= 1.2;
          return `${lost} names off the roll and no funerals, and the wall has not shifted a finger's width since.`;
        },
      },
      {
        id: 'refuse',
        label: 'Wall the lower gallery and be done.',
        rules: 'Free. Morale −0.14. All production −8% for the rest of the run, and nothing else comes up.',
        apply: (w) => {
          mood(w, -0.14);
          w.mods.resourceRate *= 0.92;
          return 'Nine courses of stone and a door with no handle on the far side. The rent has stopped being asked for.';
        },
      },
      {
        id: 'buy',
        label: 'Pay in iron and bone instead.',
        rules: 'Costs 220 iron and 90 Souls. Walls +12% health. Morale +0.10.',
        cost: { iron: 220, souls: 90 },
        apply: (w) => {
          w.mods.wallHp *= 1.12;
          mood(w, 0.1);
          return 'It was taken. It was taken slowly, and there was some doubt for a day whether it would be.';
        },
      },
    ],
  },

  {
    id: 'verdant_grove',
    kind: 'faction',
    title: 'The Grove Has An Opinion',
    summary: 'The singers will not go into the east stand.',
    flavour:
      'Something in the east stand has stopped answering the second verse and started answering a different one. The songweavers say this happens. They say it usually happens a great deal further from a settlement than this, and then they stop saying things.',
    weight: 6,
    when: (w) => w.faction.id === 'verdant' && w.wave >= 6,
    choices: [
      {
        id: 'sing',
        label: 'Sing it back.',
        rules: 'Free. −50 Will. All production +10% for the rest of the run.',
        apply: (w) => {
          grantWill(w, -50);
          w.mods.resourceRate *= 1.1;
          return 'Nine of them, four days, no sleep. The stand answers the second verse now and something else as well.';
        },
      },
      {
        id: 'cut',
        label: 'Cut the stand down.',
        rules: '+320 timber. Morale −0.20, and Will regenerates 12% slower for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { timber: 320 });
          mood(w, -0.2);
          w.mods.willRegen *= 0.88;
          return 'Good timber, cut clean, stacked and seasoning. The choir has not sung together since.';
        },
      },
      {
        id: 'ask',
        label: 'Ask it what it wants.',
        rules: 'Costs 120 Souls. Archers and towers gain 1.5 tiles of range for the rest of the run.',
        cost: { souls: 120 },
        apply: (w) => {
          w.mods.archerRange += 1.5;
          w.mods.towerRange *= 1.08;
          return 'It wanted the ditch line moved eleven feet east. It was moved. The shooting from the wall is better.';
        },
      },
    ],
  },

  {
    id: 'litany_tithe',
    kind: 'faction',
    title: 'The Grave Tithe Falls Due',
    summary: 'The reliquary is asking for a reckoning.',
    flavour:
      'The court never adjourned and it does not forget. Every so often it looks up from whatever it is doing and requires the ledger. There are a great many more names on your ground than there were, and every one of them is a subscriber.',
    weight: 6,
    when: (w) => w.faction.id === 'litany' && w.wave >= 6,
    choices: [
      {
        id: 'render',
        label: 'Render the ledger in full.',
        rules: 'Free. −40% of your Souls. Morale +0.14 and +50 Will.',
        apply: (w) => {
          const paid = take(w, 'souls', w.res.souls * 0.4);
          mood(w, 0.14);
          grantWill(w, 50);
          return `${Math.round(paid)} rendered, counted and accepted. The court has adjourned for the afternoon, which it never does.`;
        },
      },
      {
        id: 'short',
        label: 'Render it short and hope.',
        rules: 'Free. −15% of your Souls. A gamble: either nothing follows, or Will regenerates 15% slower for the rest of the run.',
        apply: (w) => {
          const paid = take(w, 'souls', w.res.souls * 0.15);
          if (chance(w.rng, 0.5)) {
            return `${Math.round(paid)} rendered. Nobody looked up. Nobody has looked up since, which is being watched.`;
          }
          w.mods.willRegen *= 0.85;
          return `${Math.round(paid)} rendered, and the discrepancy was minuted. The Hearth has been slower to answer since.`;
        },
      },
      {
        id: 'coin',
        label: 'Pay it in coin, which is not the same.',
        rules: 'Costs 300 gold. Morale −0.10, +120 Souls.',
        cost: { gold: 300 },
        apply: (w) => {
          mood(w, -0.1);
          give(w, { souls: 120 });
          return 'It was accepted. It was accepted the way a creditor accepts a promissory note from someone he lives with.';
        },
      },
    ],
  },

  {
    id: 'vigil_oath',
    kind: 'faction',
    title: 'The Nine Hundredth Oath',
    summary: 'A confessor requires a ruling.',
    flavour:
      'Nine hundred oaths, and the ninth hundred is the one about not counting the others, and a confessor has counted them. He would like to know which of the eight hundred and ninety-nine he now stands in breach of. He would like it in writing. He has not eaten for four days pending the answer.',
    weight: 6,
    when: (w) => w.faction.id === 'vigil' && w.wave >= 6,
    choices: [
      {
        id: 'absolve',
        label: 'Absolve him, in writing, and seal it.',
        rules: 'Free. Morale +0.18. Powers come back 10% slower for the rest of the run.',
        apply: (w) => {
          mood(w, 0.18);
          w.mods.powerCooldown *= 1.1;
          return 'He ate that evening. Eleven others have since asked for the same paper, and been given it.';
        },
      },
      {
        id: 'strike',
        label: 'Strike him from the roll.',
        rules: 'Free. Morale −0.16, +200 Souls, and powers hit 12% harder for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.16);
          give(w, { souls: 200 });
          w.mods.powerDamage *= 1.12;
          return 'The roll is shorter by one and the reliquary heavier by rather more than one man usually is.';
        },
      },
      {
        id: 'count',
        label: 'Have the register counted properly.',
        rules: 'Costs 150 Souls. +60 Will and Will regenerates 12% faster for the rest of the run. Morale +0.08.',
        cost: { souls: 150 },
        apply: (w) => {
          grantWill(w, 60);
          w.mods.willRegen *= 1.12;
          mood(w, 0.08);
          return 'Eight hundred and ninety-four. The order has been six short for a long time and nobody had checked.';
        },
      },
    ],
  },

  {
    id: 'moonbound_hunt',
    kind: 'faction',
    title: 'The Pack Wants The Long Howl',
    summary: 'The howlers will not come in.',
    flavour:
      'A wall is a thing you die behind, and three nights running the pack has said so at the top of the ditch where the sentries can hear it said. They want to be let out and pointed at something. They are not going to ask twice.',
    weight: 6,
    when: (w) => w.faction.id === 'moonbound' && w.wave >= 6,
    choices: [
      {
        id: 'loose',
        label: 'Loose them.',
        rules: 'Free. The coming wave loses a quarter of its strength. −4 soldiers, morale +0.18.',
        apply: (w) => {
          scaleIncoming(w, 0.75);
          const lost = loseSoldiers(w, 4);
          mood(w, 0.18);
          return `They were gone two nights. ${lost} of them stayed out there, which is understood to be the arrangement.`;
        },
      },
      {
        id: 'hold',
        label: 'Hold them at the ditch.',
        rules: 'Free. Morale −0.20. Walls +20% health for the rest of the run.',
        apply: (w) => {
          mood(w, -0.2);
          w.mods.wallHp *= 1.2;
          return 'Held, and put to work on the thing they hate. The wall has never gone up so fast or so badly liked.';
        },
      },
      {
        id: 'feed',
        label: 'Give them the herd instead.',
        rules: 'Costs 200 grain. Morale +0.12, and units deal 8% more damage for the rest of the run.',
        cost: { grain: 200 },
        apply: (w) => {
          mood(w, 0.12);
          w.mods.unitDamage *= 1.08;
          return 'The whole herd, in one night, inside the ditch, in front of everybody. They came in afterwards.';
        },
      },
    ],
  },

  {
    id: 'ashen_claim',
    kind: 'faction',
    title: 'A Claimant, With Papers',
    summary: 'Someone has arrived claiming the Crown.',
    flavour:
      'The empire could not decide whether it was dying and so did both, and the paperwork has survived rather better than the people. He has a seal, four retainers, a horse worth more than the granary, and an entirely serious claim to a throne that is presently a scorch mark.',
    weight: 6,
    when: (w) => w.faction.id === 'ashen' && w.wave >= 6,
    choices: [
      {
        id: 'kneel',
        label: 'Kneel, and let him say the words.',
        rules: 'Free. Morale +0.26. Tax set to Hard.',
        apply: (w) => {
          mood(w, 0.26);
          w.taxPolicy = TaxPolicy.Hard;
          return 'They knelt in the square in the wet and got up believing something, and paying for it by the quarter.';
        },
      },
      {
        id: 'ledger',
        label: 'Put him on the ledgers.',
        rules: 'Free. +180 gold, morale +0.06, buildings cost 6% less for the rest of the run.',
        apply: (w) => {
          give(w, { gold: 180 });
          mood(w, 0.06);
          w.mods.buildCost *= 0.94;
          return 'He is very good at it, which he does not enjoy being told. The accounts have never been in better order.';
        },
      },
      {
        id: 'road',
        label: 'Take the horse and put him on the road.',
        rules: '+240 gold and +90 iron. Morale −0.18.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { gold: 240, iron: 90 });
          mood(w, -0.18);
          return 'The seal went into the strongbox and the man went south on foot, still explaining, to nobody.';
        },
      },
    ],
  },

  // ── Dark, and the Hearth ─────────────────────────────────────────────────

  {
    id: 'hearth_spoke',
    kind: 'lore',
    title: 'The Hearth Spoke',
    summary: 'The Hearth said a word in the night.',
    flavour:
      'Not loudly, and not to everyone. The two acolytes on the night watch heard it, wrote it down separately, and the two versions agree — which they have never managed on anything else. It was a name. It was not a name either of them knew, and the register does not have it either.',
    weight: 5,
    when: (w) => w.wave >= 12 && w.will >= 40,
    choices: [
      {
        id: 'answer',
        label: 'Answer it.',
        rules: 'Free. −60 Will. Powers hit 20% harder and Will regenerates 15% faster for the rest of the run.',
        apply: (w) => {
          grantWill(w, -60);
          w.mods.powerDamage *= 1.2;
          w.mods.willRegen *= 1.15;
          return 'Said back, out loud, at the same hour. The fire went flat for a moment and then stood up taller.';
        },
      },
      {
        id: 'record',
        label: 'Write it in the register and close the book.',
        rules: 'Free. Morale +0.10 and +90 Souls.',
        apply: (w) => {
          mood(w, 0.1);
          give(w, { souls: 90 });
          return 'Entered, dated, witnessed by two, and the book shut on it. That is what a register is for.';
        },
      },
      {
        id: 'bank',
        label: 'Bank the Hearth down until it stops.',
        rules: 'Free. Morale −0.18. All production +12% and Will regenerates 20% slower for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.18);
          w.mods.resourceRate *= 1.12;
          w.mods.willRegen *= 0.8;
          return 'Ash and turves over it for two nights. Everybody worked, because nobody wanted to stand near it.';
        },
      },
    ],
  },

  {
    id: 'own_register',
    kind: 'lore',
    title: 'The Register Reads Back',
    summary: 'The chronicler wants a word about the book.',
    flavour:
      'He keeps the register because somebody has to and he has kept it since the first wave, and this morning he brings it in open at a page he did not write. The hand is his. The entries are in order and they are dated ahead. He would like to know whether to keep going.',
    weight: 5,
    when: (w) => w.wave >= 10 && w.eventHistory.length >= 6,
    choices: [
      {
        id: 'keep',
        label: 'Tell him to keep writing.',
        rules: 'Free. Morale −0.08, +140 Souls and +40 Will.',
        apply: (w) => {
          mood(w, -0.08);
          give(w, { souls: 140 });
          grantWill(w, 40);
          return 'He kept writing. He has stopped reading back what he wrote, which he says is a separate discipline.';
        },
      },
      {
        id: 'burn',
        label: 'Burn the book and start a new one.',
        rules: 'Free. Morale +0.16. All production +6% for the rest of the run.',
        apply: (w) => {
          mood(w, 0.16);
          w.mods.resourceRate *= 1.06;
          return 'A new book, ruled and dated from today. The settlement worked better for not being on a page.';
        },
      },
      {
        id: 'read',
        label: 'Read the pages he did not write.',
        rules: 'Costs 140 Souls. The coming wave loses a third of its strength. Morale −0.20.',
        cost: { souls: 140 },
        tone: LogTone.Bad,
        apply: (w) => {
          scaleIncoming(w, 0.66);
          mood(w, -0.2);
          return 'Four pages. Everything on the first three has since happened. The fourth is why the ditch was moved.';
        },
      },
    ],
  },

  {
    id: 'survey_open',
    kind: 'lore',
    title: 'The Survey Does Not Close',
    summary: 'The map of the workings will not add up.',
    flavour:
      'The quarry survey has been run four times by three different men, and it comes out longer inside than outside by eleven feet, every time. The last surveyor has asked to be paid in full and released, and has not asked for a reference.',
    weight: 4,
    when: (w) => w.wave >= 14,
    choices: [
      {
        id: 'seal',
        label: 'Seal the eleven feet and forget them.',
        rules: '−90 stone. Nothing follows.',
        apply: (w) => {
          take(w, 'stone', 90);
          return 'Bricked, rendered, and left off the new survey entirely, which is the closest thing to forgetting available.';
        },
      },
      {
        id: 'measure',
        label: 'Measure it a fifth time, with the acolytes.',
        rules: 'A gamble: either +320 Souls and +50 Will, or −5 villagers and morale −0.22.',
        apply: (w) => {
          if (chance(w.rng, 0.5)) {
            give(w, { souls: 320 });
            grantWill(w, 50);
            return 'Eleven feet, and something in them that came out in a barrow under a cloth and went to the furnace.';
          }
          const lost = loseVillagers(w, 5);
          mood(w, -0.22);
          return `The chain went in and did not come back. ${lost} of the party went in after it, on the same principle.`;
        },
      },
      {
        id: 'build',
        label: 'Build into it.',
        rules: 'Costs 200 stone and 100 iron. Storage +400, all production +8% for the rest of the run. Morale −0.14.',
        cost: { stone: 200, iron: 100 },
        apply: (w) => {
          w.mods.storage += 400;
          recomputeStorage(w);
          w.mods.resourceRate *= 1.08;
          mood(w, -0.14);
          return 'Cellars, and good ones, and eleven feet of them that the masons will not work in after dark.';
        },
      },
    ],
  },

  {
    id: 'reliquary_full',
    kind: 'lore',
    title: 'The Reliquary Is Full, And Still Asking',
    summary: 'There is no more room on the shelves.',
    flavour:
      'The relics have been rehoused three times and the last move needed a wall taken out. They do not sit quietly beside one another. The keeper has begun writing down which of them must not be shelved within six feet of which others, and that list is now longer than the inventory.',
    weight: 4,
    when: (w) => w.wave >= 10 && w.relics.length >= 3,
    choices: [
      {
        id: 'shelve',
        label: 'Build them a proper house.',
        rules: 'Costs 160 stone and 160 timber. +80 Will, and powers hit 10% harder for the rest of the run.',
        cost: { stone: 160, timber: 160 },
        apply: (w) => {
          grantWill(w, 80);
          w.mods.powerDamage *= 1.1;
          return 'Stone, six bays, one door, and the keeper sleeping across it because he says he prefers to.';
        },
      },
      {
        id: 'melt',
        label: 'Melt down the three least useful.',
        rules: 'Free. +400 Souls and +240 iron. Morale −0.20.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { souls: 400, iron: 240 });
          mood(w, -0.2);
          return 'Two went into the crucible quietly. The third took a day and a half and the smiths worked in shifts.';
        },
      },
      {
        id: 'bury',
        label: 'Bury the noisiest one under the Hearth.',
        rules: 'Free. Morale +0.14, and Will regenerates 15% faster for the rest of the run.',
        apply: (w) => {
          mood(w, 0.14);
          w.mods.willRegen *= 1.15;
          return 'Four feet down under the fire, on its side, the way the keeper insisted. The shelves are quiet now.';
        },
      },
    ],
  },

  {
    id: 'plans_read',
    kind: 'lore',
    title: 'The Plans Are Being Read',
    summary: 'Someone else has a copy of the work.',
    flavour:
      'The Age is bought and the drawings are out on every table in the hall, which is how work of that size is done and also how work of that size gets out. A sheet is missing from the third set. It is the sheet with the gate detail on it.',
    weight: 7,
    when: (w) => w.researchingAge > 0,
    choices: [
      {
        id: 'redraw',
        label: 'Redraw the gate detail, and pay for the delay.',
        rules: 'Costs 240 gold. Nothing follows.',
        cost: { gold: 240 },
        apply: () =>
          'Redrawn from scratch by two men who were not in the hall that week. The old set was burned in front of them.',
      },
      {
        id: 'hunt',
        label: 'Turn the hall out and find the sheet.',
        rules: 'Free. Morale −0.18. The coming wave loses a fifth of its strength.',
        tone: LogTone.Bad,
        apply: (w) => {
          mood(w, -0.18);
          scaleIncoming(w, 0.8);
          return 'Every chest in the hall, and then every chest in the row behind it. The sheet was in a chest.';
        },
      },
      {
        id: 'ignore',
        label: 'Let them read it. Keep building.',
        rules: 'Free. The coming wave gains a third of its strength. Buildings finish 15% faster for the rest of the run.',
        tone: LogTone.Dire,
        apply: (w) => {
          scaleIncoming(w, 1.33);
          w.mods.buildSpeed *= 1.15;
          return 'Nobody was pulled off a bench to look for it, and the work is eleven days ahead of where it should be.';
        },
      },
    ],
  },

  {
    id: 'nothing_to_sell',
    kind: 'lore',
    title: 'Nothing Left To Sell',
    summary: 'The treasury is a room with a table in it.',
    flavour:
      'The paymaster has stopped coming. The tax farmer has stopped coming. There is a room with a table in it and a book on the table, and the writing in the book gets smaller down the page. Outside, the wall is shorter than it was.',
    weight: 9,
    when: (w) => w.wave >= 6 && w.res.gold < 60 && w.arrears > 15 && w.morale < 0.5,
    choices: [
      {
        id: 'bells',
        label: 'Take down the chapel bells and sell the metal.',
        rules: 'Free. +260 gold and +120 iron. Morale −0.24, Will regenerates 15% slower for the rest of the run.',
        tone: LogTone.Bad,
        apply: (w) => {
          give(w, { gold: 260, iron: 120 });
          mood(w, -0.24);
          w.mods.willRegen *= 0.85;
          return 'Down in a morning, out on a cart by noon. Nothing has been rung for anything since.';
        },
      },
      {
        id: 'bond',
        label: 'Sell next year, on paper, to whoever will sign.',
        rules: 'Free. +380 gold. All production −12% for the rest of the run.',
        apply: (w) => {
          give(w, { gold: 380 });
          w.mods.resourceRate *= 0.88;
          return 'Signed, sealed and carted south. Every bench in the settlement is now working off a piece of paper.';
        },
      },
      {
        id: 'render',
        label: 'Render the reliquary down and pay the men.',
        rules: 'Costs 60 Souls. Arrears cleared, +200 gold. Morale −0.10.',
        cost: { souls: 60 },
        apply: (w) => {
          w.arrears = 0;
          give(w, { gold: 200 });
          mood(w, -0.1);
          return 'The furnace ran two nights and the garrison was paid on the third. Both facts are known to everyone.';
        },
      },
    ],
  },

];

const BY_ID = new Map<string, ChronicleEventDef>();
for (const e of CHRONICLE) BY_ID.set(e.id, e);

export function chronicleEvent(id: string): ChronicleEventDef | undefined {
  return BY_ID.get(id);
}

export function chronicleChoice(
  eventId: string,
  choiceId: string,
): ChronicleChoice | undefined {
  return BY_ID.get(eventId)?.choices.find((c) => c.id === choiceId);
}

/**
 * Everything that could fire right now and has not used up its allowance.
 *
 * Most cards fire once a run. The ones that carry a `repeat` are the ones a
 * settlement genuinely lives through more than once — a harvest, a fire, a wind
 * off the Rotmoot ground — and the count comes off the history rather than off a
 * counter of its own, so there is nothing extra to hash and nothing extra to
 * save.
 */
export function eligibleEvents(w: World, history: readonly string[]): ChronicleEventDef[] {
  const seen = new Map<string, number>();
  for (const h of history) {
    const id = h.slice(0, h.indexOf('>'));
    seen.set(id, (seen.get(id) ?? 0) + 1);
  }
  return CHRONICLE.filter(
    (e) =>
      (seen.get(e.id) ?? 0) < (e.repeat ?? 1) && e.when(w) && offeredChoices(w, e).length > 0,
  );
}

/** The choice ids this world is actually allowed to see, in deck order. */
export function offeredChoices(w: World, def: ChronicleEventDef): string[] {
  return def.choices.filter((c) => !c.when || c.when(w)).map((c) => c.id);
}
